import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IGetUserProfileThunkArgs,
	IUpdateUserProfileThunkArgs,
	IChangePasswordThunkArgs,
} from 'src/features/profile/domain/types';
import { userProfileApi } from 'src/features/profile/infrastructure/api';
import { IUser } from 'src/features/user';
import { handleThunkError } from 'src/utils/handleThunkError';

export const USER_PROFILE_LOADING_KEY = 'user_profile';

export const getUserProfile = createAsyncThunk<
	IUser,
	IGetUserProfileThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user_profile/getUserProfile',
	async ({ onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: true }),
			);
			const response = await userProfileApi.getUserProfile();

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch user profile');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const updateUserProfile = createAsyncThunk<
	IUser,
	IUpdateUserProfileThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user_profile/updateUserProfile',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: true }),
			);
			const response = await userProfileApi.updateUserProfile(request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to update user profile',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: false }),
			);
		}
	},
);

export const changePassword = createAsyncThunk<
	void,
	IChangePasswordThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'user_profile/changePassword',
	async ({ request, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: true }),
			);
			const response = await userProfileApi.changePassword(request);

			onSuccess?.();
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to change password');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({ key: USER_PROFILE_LOADING_KEY, loading: false }),
			);
		}
	},
);
